import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  toast,
} from "@mindstonehq/ui";
import React, { Fragment, useEffect, useState } from "react";
import { Link, useRevalidator } from "react-router";
import { useMutation, useQuery } from "@apollo/client/react";
import type {
  MindspaceCardFragment,
  FullResourceFragment,
  MindspacesQuery,
  MindspacesQueryVariables,
} from "~/graphql/codegen/graphql";
import useAuthCheck from "~/hooks/check-authenticated";
import copy from "copy-to-clipboard";
import metrics from "~/utils/metrics";
import { SHARE_RESOURCE_MUTATION, MINDSPACE_ADD_ARTICLES_MUTATION, MINDSPACES_QUERY } from "../annotate-mutations";
import { CheckCircleIcon, ShareIcon, SquaresPlusIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { ChevronLeftIcon } from "@heroicons/react/20/solid";
import { CopyIcon } from "~/components/icons";
import { EmailsInput } from "~/components/emails-input";
import { Trans, useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";

export function DialogShareArticle({
  article,
  ...props
}: React.PropsWithChildren<{
  article: FullResourceFragment;
  onOpenChange?: (open: Boolean) => any;
}>) {
  const { t } = useLingui();
  const [open, setOpen] = useState(false);
  const { revalidate } = useRevalidator();
  const [emails, setEmails] = React.useState<string[]>([]);
  const [submitting, setSubmitting] = React.useState(false);
  const [uiState, setUiState] = React.useState<"default" | "moveToMindspace" | "movedToMindspace" | "sendEmailSuccess">(
    "default",
  );
  const focusRef = React.useRef<HTMLButtonElement>(null);
  const [shareResourceMutation] = useMutation(SHARE_RESOURCE_MUTATION);
  const [addToMindspace] = useMutation(MINDSPACE_ADD_ARTICLES_MUTATION);

  const checkAuth = useAuthCheck();
  useEffect(() => {
    // hack to overwrite headless ui putting focus on twitter button
    setTimeout(() => {
      if (focusRef.current) {
        focusRef?.current.blur();
      }
    }, 0);
  }, [uiState]);

  const [selectedMindspace, setSelectedMindspace] = useState<MindspaceCardFragment | null>(null);
  const [copyAnnotations, setCopyAnnotations] = useState(true);
  const { data, loading } = useQuery<MindspacesQuery, MindspacesQueryVariables>(MINDSPACES_QUERY);

  const mindspaces = (data?.mindspaces.all || []).filter((m) => m.currentUserPermission?.canAddResource);

  const handleShareOnTwitter = () => {};

  const handleCopyLink = () => {
    copy(article.publishedUrl || `https://app.mindstone.com/annotate/${article.id}`);
    toast.success(t(msg`Link copied to clipboard`));
  };

  const handleMoveToMindspace = () => {
    setUiState("moveToMindspace");
  };

  const moveToMindspace = (mindspaceId: string, mindspaceName: string, copyAnnotation: boolean) => {
    addToMindspace({
      variables: {
        articleIds: [article.id],
        mindspaceId,
        copyAnnotation,
      },
    })
      .then(() => {
        toast.success(t(msg`Resource added to ${mindspaceName}`));
        setUiState("movedToMindspace");
      })
      .catch((error) => {
        toast.error(t(msg`We couldn't add this resource to the Mindspace.`));
        throw error;
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const handleSendInvites = async () => {
    const authenticated = checkAuth(
      t(msg`You need to be logged in to use this feature`),
      `${location.href}`,
      "share_via_email",
    );
    if (!authenticated) {
      return;
    }
    setSubmitting(true);
    try {
      metrics.track("article_share_email", { article_id: article.id });

      await shareResourceMutation({
        variables: {
          articleId: article.id as string,
          shareReason: "",
          emails: emails,
        },
      });
      setEmails([]);
      setUiState("sendEmailSuccess");
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!open) {
          setUiState("default");
        }
        if (props.onOpenChange) {
          props.onOpenChange(o);
        }
      }}
    >
      <DialogTrigger asChild>{props.children}</DialogTrigger>
      <DialogContent>
        {uiState === "default" && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center flex-row space-x-2">
                <ShareIcon className="h-6 w-6" aria-hidden="true" />
                <span><Trans>Share this resource</Trans></span>
              </DialogTitle>
            </DialogHeader>
            <Button variant={"ghost"} className={"space-x-2 justify-start"} onClick={handleCopyLink}>
              <CopyIcon className={"h-5 w-5"} />
              <span><Trans>Copy link</Trans></span>
            </Button>
            {article.canAddToMindspace && mindspaces.length > 0 && (
              <Button variant={"ghost"} className={"space-x-2 justify-start"} onClick={handleMoveToMindspace}>
                <SquaresPlusIcon className={"h-5 w-5"} />
                <span><Trans>Copy to space</Trans></span>
              </Button>
            )}
            <hr className="my-3" />
            <Label><Trans>Share with friends</Trans></Label>
            <div className="flex items-center space-x-2">
              <EmailsInput
                placeholder={t(msg`Add email address`)}
                emails={emails}
                onChange={(_emails: string[]) => {
                  setEmails(_emails);
                }}
              />
            </div>
            {emails.length > 20 && (
              <div className="rounded-md bg-destructive/10 p-4 mt-2">
                <div className="flex">
                  <div className="shrink-0">
                    <XCircleIcon className="h-5 w-5 text-destructive" aria-hidden="true" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-destructive">
                      <Trans>You can't invite more than 20 people in a mindspace.</Trans>
                    </h3>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button onClick={handleSendInvites} disabled={submitting || emails.length > 20 || emails.length === 0}>
                <Trans>Share</Trans>
              </Button>
            </DialogFooter>
          </>
        )}
        {uiState === "moveToMindspace" && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center flex-row space-x-2">
                <Button variant="ghost" onClick={() => setUiState("default")} className="p-1">
                  <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" />
                </Button>
                <span><Trans>Copy to space</Trans></span>
              </DialogTitle>
            </DialogHeader>
            <p>
              {" "}
              <Trans>
                Choose your preferred Mindspace and decide whether to keep or exclude the existing comments from this
                resource.
              </Trans>
            </p>
            {mindspaces.length ? (
              <>
                <div className="grid w-full items-center gap-2">
                  <Label htmlFor="reason"><Trans>Mindspace</Trans></Label>
                  <Select
                    name="mindspace"
                    required={true}
                    onValueChange={(v) => {
                      setSelectedMindspace(mindspaces.find((x) => x.id === v) || null);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t(msg`Select a Mindspace`)} />
                    </SelectTrigger>
                    <SelectContent>
                      {mindspaces.map((x) => {
                        return (
                          <SelectItem key={x.id} value={x.id}>
                            {x.name}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-row justify-center w-full items-center gap-2">
                  <div className="flex flex-col w-full">
                    <Label><Trans>Duplicate your comments</Trans></Label>
                    <div className="text-sm text-muted-foreground">
                      <Trans>Everyone in the Mindspace will have access to them</Trans>
                    </div>
                  </div>
                  <Switch className="shrink-0" checked={copyAnnotations} onCheckedChange={setCopyAnnotations} />
                </div>
                <DialogFooter>
                  <Button
                    disabled={!selectedMindspace}
                    onClick={() => {
                      moveToMindspace(selectedMindspace?.id || "", selectedMindspace?.name || "", copyAnnotations);
                    }}
                  >
                    <Trans>Copy</Trans>
                  </Button>
                </DialogFooter>
              </>
            ) : (
              <DialogFooter>
                <DialogClose asChild>
                  <Button><Trans>Close</Trans></Button>
                </DialogClose>
              </DialogFooter>
            )}
          </>
        )}
        {uiState === "movedToMindspace" && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center flex-row space-x-2">
                <CheckCircleIcon className="h-6 w-6" aria-hidden="true" />
                <span><Trans>Resource copied to mindspace</Trans></span>
              </DialogTitle>
            </DialogHeader>
            <p><Trans>Your resource was successfully copied to your mindspace</Trans></p>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline"><Trans>Close</Trans></Button>
              </DialogClose>
            </DialogFooter>
          </>
        )}
        {uiState === "sendEmailSuccess" && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center flex-row space-x-2">
                <CheckCircleIcon className="h-6 w-6" aria-hidden="true" />
                <span><Trans>Email sent</Trans></span>
              </DialogTitle>
            </DialogHeader>
            <p><Trans>Your article was successfully shared with your friends</Trans></p>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline"><Trans>Close</Trans></Button>
              </DialogClose>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
